import { observer } from "mobx-react";
import React from "react";
import RecurringHeaderIcon from "../../../../../images/membership/recurring-devy-grey.svg";
import { PricingPageService } from "../services";
import {
  CustomPricingInputModel,
  PackagePricingInputModel,
  PricePageRecurringInputModel,
  PriceTierInputData,
  PricingPageInputModel,
  StandardPricingInputModel,
} from "../models";
import {
  BillingStartFromInterval,
  ItemBillingEnds,
  ItemBillingEndsAfterInterval,
  ItemBillingStartAfterInterval,
  ItemIncreasePriceInterval,
  PricingModel,
  RecurringBillingInterval,
} from "../enums";
import StandardPricing from "./StandardPricing";
import TierPricingComponent from "./TierPricingComponent";
import PackagePricing from "./PackagePricing";
import CustomPricing from "./CustomPricing";
import {
  DescriptionCell,
  LabelCell,
  ProductDetailWrapper,
  RecurringWrapper,
  SetupDetails,
} from "../styles";
import {
  Collapsible,
  CustomLabel,
  FlexContainer,
  InfoIcon,
  Input,
  SingleSelect,
} from "@mindmemobile/mm-shared-global";
import PricingOptionRow from "./PricingOptionRow";

type Props = {
  pricingPageData?: PricingPageService;
  index: number;
  showPricingOptions?: boolean;
};

export const getBillingOptions = (
  fieldName: keyof PricePageRecurringInputModel,
  isValueMoreThanOne: boolean = true
): any[] => {
  switch (fieldName) {
    case "billingInterval":
      return Object.keys(RecurringBillingInterval).map((key: string) => {
        const labelBase =
          RecurringBillingInterval[
            key as keyof typeof RecurringBillingInterval
          ];
        const label = isValueMoreThanOne ? `${labelBase}s` : labelBase;
        return {
          value: key,
          label,
        };
      });
    case "billingStartInterval":
      return Object.keys(BillingStartFromInterval).map((key: string) => {
        return {
          value: key,
          label:
            BillingStartFromInterval[
              key as keyof typeof BillingStartFromInterval
            ],
        };
      });
    case "billingEnds":
      return Object.keys(ItemBillingEnds).map((key: string) => {
        return {
          value: key,
          label: ItemBillingEnds[key as keyof typeof ItemBillingEnds],
        };
      });
    case "billingStartAfterInterval":
      return Object.keys(ItemBillingStartAfterInterval).map((key: string) => {
        const labelBase =
          ItemBillingStartAfterInterval[
            key as keyof typeof ItemBillingStartAfterInterval
          ];
        const label = isValueMoreThanOne ? `${labelBase}s` : labelBase;
        return {
          value: key,
          label,
        };
      });
    case "billingEndsAfterInterval":
      return Object.keys(ItemBillingEndsAfterInterval).map((key: string) => {
        const labelBase =
          ItemBillingEndsAfterInterval[
            key as keyof typeof ItemBillingEndsAfterInterval
          ];
        const label = !isValueMoreThanOne ? labelBase.slice(0, -1) : labelBase;
        return {
          value: key,
          label,
        };
      });
  }
  return [];
};

class RecurringSection extends React.Component<Props> {
  get editablePrice() {
    return this.props.pricingPageData?.prices[this.props.index];
  }

  get isOpen(): boolean {
    return this.editablePrice?.isRecurringOpen === true;
  }

  toggleRecurringSection = () => {
    this.props.pricingPageData?.updatePrice(
      new PricingPageInputModel({
        ...this.editablePrice,
        isRecurringOpen: !this.isOpen,
      }),
      this.props.index
    );
  };

  updatePricing = (standardPricing: StandardPricingInputModel) => {
    if (!this.editablePrice) return;
    this.props.pricingPageData?.updatePrice(
      new PricingPageInputModel({
        ...this.editablePrice,
        recurringSettings: new PricePageRecurringInputModel({
          ...this.editablePrice.recurringSettings,
          standardPricing: standardPricing,
        }),
      }),
      this.props.index
    );
  };

  updateValue(
    value: string | boolean,
    fieldName: keyof PricePageRecurringInputModel
  ) {
    if (!this.editablePrice?.recurringSettings) return;
    let recurrenceSettings = new PricePageRecurringInputModel({
      ...this.editablePrice.recurringSettings,
    });
    switch (fieldName) {
      case "billingEndIntervalCount":
        recurrenceSettings = new PricePageRecurringInputModel({
          ...this.editablePrice.recurringSettings,
          billingEndIntervalCount: value as string,
        });
        break;
      case "billingEnds":
        recurrenceSettings = new PricePageRecurringInputModel({
          ...this.editablePrice.recurringSettings,
          billingEnds: value as keyof typeof ItemBillingEnds,
        });
        break;
      case "billingStartInterval":
        recurrenceSettings = new PricePageRecurringInputModel({
          ...this.editablePrice.recurringSettings,
          billingStartInterval: value as BillingStartFromInterval,
        });
        break;
      case "billingInterval":
        const newBillingInterval =
          value as keyof typeof RecurringBillingInterval;
        const isGreaterThan3Months =
          recurrenceSettings?.isBillEveryGreaterThan3Months(
            newBillingInterval,
            recurrenceSettings?.billingIntervalCount
          );
        recurrenceSettings = new PricePageRecurringInputModel({
          ...recurrenceSettings,
          billingInterval: newBillingInterval,
          increasePriceInterval: isGreaterThan3Months
            ? recurrenceSettings?.increasePriceInterval
            : "None",
          feeInterval: isGreaterThan3Months
            ? recurrenceSettings?.feeInterval
            : "None",
        });
        break;
      case "billingStartAfterInterval":
        recurrenceSettings = new PricePageRecurringInputModel({
          ...this.editablePrice.recurringSettings,
          billingStartAfterInterval: value as ItemBillingStartAfterInterval,
        });
        break;
      case "billingStartIntervalCount":
        recurrenceSettings = new PricePageRecurringInputModel({
          ...this.editablePrice.recurringSettings,
          billingStartIntervalCount: value.toString(),
        });
        break;
      case "billingIntervalCount":
        const newBillingIntervalCount = value.toString();
        const isGreaterThan3Monthss =
          recurrenceSettings?.isBillEveryGreaterThan3Months(
            recurrenceSettings?.billingInterval,
            newBillingIntervalCount
          );
        recurrenceSettings = new PricePageRecurringInputModel({
          ...recurrenceSettings,
          billingIntervalCount: newBillingIntervalCount,
          increasePriceInterval: isGreaterThan3Monthss
            ? recurrenceSettings?.increasePriceInterval
            : "None",
          feeInterval: isGreaterThan3Monthss
            ? recurrenceSettings?.feeInterval
            : "None",
        });
        break;
      case "prorateFirstPayment":
        recurrenceSettings = new PricePageRecurringInputModel({
          ...this.editablePrice.recurringSettings,
          prorateFirstPayment: Boolean(value),
        });
        break;

      case "billingEndsAfterInterval":
        recurrenceSettings = new PricePageRecurringInputModel({
          ...this.editablePrice.recurringSettings,
          billingEndsAfterInterval:
            value as keyof typeof ItemBillingEndsAfterInterval,
        });
        break;
      case "pricingModel":
        recurrenceSettings = new PricePageRecurringInputModel({
          ...this.editablePrice.recurringSettings,
          pricingModel: value as PricingModel,
        });
        break;
    }
    this.props.pricingPageData?.updatePrice(
      new PricingPageInputModel({
        ...this.editablePrice,
        recurringSettings: recurrenceSettings,
      }),
      this.props.index
    );
  }

  hasError(fieldName: keyof PricePageRecurringInputModel) {
    if (!this.editablePrice?.recurringSettings) return false;

    return (
      this.editablePrice?.recurringSettings?.errors?.find(
        (error) => error.field === fieldName
      ) !== undefined
    );
  }

  numberField(value: string, fieldName: keyof PricePageRecurringInputModel) {
    return (
      <Input
        value={value}
        type="number"
        error={this.hasError(fieldName)}
        className="styled-input number-selector"
        onChange={(e) => this.updateValue(e.target.value, fieldName)}
      />
    );
  }

  singleSelect(
    value: string,
    fieldName: keyof PricePageRecurringInputModel,
    isCountMoreThanOne: boolean = true
  ) {
    return (
      <SingleSelect
        searchable={false}
        selectedKey={value}
        nameKeys={["label"]}
        valueKeys={["value"]}
        options={[getBillingOptions(fieldName, isCountMoreThanOne)]}
        onChange={(option) => this.updateValue(option.value, fieldName)}
        className="dpdn-no-separator"
        hasError={this.hasError(fieldName)}
      />
    );
  }

  get billingEnds() {
    switch (this.editablePrice?.recurringSettings?.billingEnds) {
      case ItemBillingEnds.After:
        return (
          <>
            {this.singleSelect(
              this.editablePrice?.recurringSettings?.billingEnds || "",
              "billingEnds"
            )}
            {this.numberField(
              this.editablePrice?.recurringSettings?.billingEndIntervalCount ||
                "",
              "billingEndIntervalCount"
            )}
            {this.singleSelect(
              this.editablePrice?.recurringSettings?.billingEndsAfterInterval ||
                "",
              "billingEndsAfterInterval",
              parseFloat(
                this.editablePrice?.recurringSettings?.billingEndIntervalCount
              ) > 1
            )}
          </>
        );
      default:
        return (
          <>
            {this.singleSelect(
              this.editablePrice?.recurringSettings?.billingEnds || "",
              "billingEnds"
            )}
          </>
        );
    }
  }

  get billingStarts() {
    switch (this.editablePrice?.recurringSettings?.billingStartInterval) {
      case BillingStartFromInterval.After:
        return (
          <>
            {this.singleSelect(
              this.editablePrice?.recurringSettings?.billingStartInterval || "",
              "billingStartInterval"
            )}
            {this.numberField(
              this.editablePrice?.recurringSettings
                ?.billingStartIntervalCount || "",
              "billingStartIntervalCount"
            )}
            {this.singleSelect(
              this.editablePrice?.recurringSettings
                ?.billingStartAfterInterval || "",
              "billingStartAfterInterval",
              parseFloat(
                this.editablePrice?.recurringSettings?.billingStartIntervalCount
              ) > 1
            )}
          </>
        );
      default:
        return (
          <>
            {this.singleSelect(
              this.editablePrice?.recurringSettings?.billingStartInterval || "",
              "billingStartInterval"
            )}
            {/* Sunil asked to hide this */}
            {/* <Input
                          type="checkbox"
                          label={'Prorate First Payment'}
                          value={this.editablePrice?.recurringSettings?.prorateFirstPayment === true}
                          onChange={(e) => this.updateValue(e.target.checked, 'prorateFirstPayment')}
                      /> */}
          </>
        );
    }
  }

  get volumePrice(): number {
    return (
      parseFloat(
        this.editablePrice?.recurringSettings?.volumePricing?.price || ""
      ) || 0
    );
  }

  get tieredPrice(): number {
    return (
      parseFloat(
        this.editablePrice?.recurringSettings?.tieredPricing?.price || ""
      ) || 0
    );
  }

  get getPricingInputs(): React.ReactNode {
    switch (this.editablePrice?.recurringSettings?.pricingModel) {
      case PricingModel.Standard:
        return (
          <StandardPricing
            standardPricing={
              this.editablePrice?.recurringSettings?.standardPricing ||
              new StandardPricingInputModel({ showSetUpFee: true })
            }
            onUpdate={(updatedStandardPricing) =>
              this.updatePricingOptions(
                updatedStandardPricing,
                "standardPricing"
              )
            }
          />
        );
      case PricingModel.Volume:
        return (
          <TierPricingComponent
            totalPrice={this.volumePrice}
            pricingType={PricingModel.Volume}
            tier={
              this.editablePrice?.recurringSettings?.volumePricing ||
              new PriceTierInputData({ showSetUpFee: true })
            }
            onUpdateTier={(updatedValue) =>
              this.updatePricingOptions(updatedValue, "volumePricing")
            }
          />
        );
      case PricingModel.Tiered:
        return (
          <TierPricingComponent
            totalPrice={this.tieredPrice}
            pricingType={PricingModel.Tiered}
            tier={
              this.editablePrice?.recurringSettings?.tieredPricing ||
              new PriceTierInputData({ showSetUpFee: true })
            }
            onUpdateTier={(updatedValue) =>
              this.updatePricingOptions(updatedValue, "tieredPricing")
            }
          />
        );
      case PricingModel.Package:
        return (
          <PackagePricing
            package={
              this.editablePrice?.recurringSettings?.packagePricing ||
              new PackagePricingInputModel({ showSetUpFee: true })
            }
            onUpdate={(updatedValue) =>
              this.updatePricingOptions(updatedValue, "packagePricing")
            }
          />
        );
      case PricingModel.Custom:
        return (
          <CustomPricing
            onUpdate={(updatedValue) =>
              this.updatePricingOptions(updatedValue, "customPricing")
            }
            customPricing={
              this.editablePrice?.recurringSettings?.customPricing ||
              new CustomPricingInputModel({ showSetUpFee: true })
            }
          />
        );
      default:
        return <></>;
    }
  }

  updatePricingOptions(
    value:
      | StandardPricingInputModel
      | PackagePricingInputModel
      | PriceTierInputData
      | CustomPricingInputModel,
    fieldName: keyof PricePageRecurringInputModel
  ) {
    if (!this.editablePrice?.recurringSettings) return;
    let recurringSettings = new PricePageRecurringInputModel({
      ...this.editablePrice.recurringSettings,
    });
    switch (fieldName) {
      case "standardPricing":
        recurringSettings = new PricePageRecurringInputModel({
          ...this.editablePrice.recurringSettings,
          standardPricing: value as StandardPricingInputModel,
        });
        break;
      case "packagePricing":
        recurringSettings = new PricePageRecurringInputModel({
          ...this.editablePrice.recurringSettings,
          packagePricing: value as PackagePricingInputModel,
        });
        break;
      case "volumePricing":
        recurringSettings = new PricePageRecurringInputModel({
          ...this.editablePrice.recurringSettings,
          volumePricing: value as PriceTierInputData,
        });
        break;
      case "tieredPricing":
        recurringSettings = new PricePageRecurringInputModel({
          ...this.editablePrice.recurringSettings,
          tieredPricing: value as PriceTierInputData,
        });
        break;
      case "customPricing":
        recurringSettings = new PricePageRecurringInputModel({
          ...this.editablePrice.recurringSettings,
          customPricing: value as CustomPricingInputModel,
        });
        break;
    }
    this.props.pricingPageData?.updatePrice(
      new PricingPageInputModel({
        ...this.editablePrice,
        recurringSettings: recurringSettings,
      }),
      this.props.index
    );
  }
  render(): React.ReactNode {
    return (
      <RecurringWrapper>
        <ProductDetailWrapper>
          <SetupDetails>
            <CustomLabel
              fontSize="22px"
              fontWeight="bold"
              lineHeight="22px"
              margin="40px 0px 0px"
              className="setup"
            >
              <img src={RecurringHeaderIcon} /> Recurring
              <i
                className={`fa fa-dark-grey clickable ${this.isOpen ? "fa-chevron-down" : "fa-chevron-right"}`}
                onClick={() => this.toggleRecurringSection()}
              ></i>
            </CustomLabel>
            <Collapsible isOpen={this.editablePrice?.isRecurringOpen === true}>
              {this.props.showPricingOptions && (
                <PricingOptionRow
                  hasError={this.hasError("pricingModel")}
                  selectedValue={
                    this.editablePrice?.recurringSettings?.pricingModel || ""
                  }
                  onChange={(option) =>
                    this.updateValue(option, "pricingModel")
                  }
                />
              )}
              {this.getPricingInputs}
              <FlexContainer className="label-description-row">
                <LabelCell>
                  <CustomLabel lineHeight="40px" fontSize="16px">
                    Bill Every
                  </CustomLabel>
                  <InfoIcon />
                </LabelCell>
                <DescriptionCell
                //  className="padding-right-0"
                >
                  <FlexContainer
                    spacing={15}
                    fxLayout="row"
                    className="price-field"
                  >
                    {this.numberField(
                      this.editablePrice?.recurringSettings
                        ?.billingIntervalCount || "",
                      "billingIntervalCount"
                    )}
                    {this.singleSelect(
                      this.editablePrice?.recurringSettings?.billingInterval ||
                        "",
                      "billingInterval",
                      parseFloat(
                        this.editablePrice?.recurringSettings
                          ?.billingIntervalCount || "0"
                      ) > 1
                    )}
                  </FlexContainer>
                </DescriptionCell>
              </FlexContainer>
              <FlexContainer className="label-description-row billing-starts-row">
                <LabelCell>
                  <CustomLabel lineHeight="40px" fontSize="16px">
                    Billing Starts
                  </CustomLabel>
                  <InfoIcon />
                </LabelCell>
                <DescriptionCell>
                  <FlexContainer spacing={15} fxWrap="wrap">
                    {this.billingStarts}
                  </FlexContainer>
                </DescriptionCell>
              </FlexContainer>
              <FlexContainer className="label-description-row">
                <LabelCell>
                  <CustomLabel lineHeight="40px" fontSize="16px">
                    Billing Ends
                  </CustomLabel>
                  <InfoIcon />
                </LabelCell>
                <DescriptionCell>
                  <FlexContainer
                    spacing={15}
                    fxWrap="wrap"
                    className="price-field"
                  >
                    {this.billingEnds}
                  </FlexContainer>
                </DescriptionCell>
              </FlexContainer>
            </Collapsible>
          </SetupDetails>
        </ProductDetailWrapper>
      </RecurringWrapper>
    );
  }
}

export default observer(RecurringSection);
